import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Button, Callout } from '@/components/ui';
import type { LowStockRow } from '@/lib/stock-types';
import { suggestedQty } from '@/lib/reorder';

/**
 * Raise a purchase order for everything under one supplier. R-163, task D018.
 *
 * `POST /api/purchase-orders` was written and ticked, and **nothing called it**
 * (R-162). The purchase orders screen lists orders, sends them and receives
 * against them; it could not create one, so there was no way to order stock
 * from this system at all.
 *
 * The button belongs here rather than there, because this is the screen that
 * already knows what is short and who supplies it. The order it raises is a
 * DRAFT: `send` is a separate, deliberate act, and the purchase orders screen
 * is where somebody checks it before it goes to the supplier.
 *
 * Quantities are the reorder quantity, which is the number the studio set for
 * exactly this moment. An item with no reorder quantity is left out and said
 * so, rather than having a number invented for it.
 */
export function RaisePurchaseOrder({
  supplierId,
  supplierName,
  rows,
}: {
  supplierId: string;
  supplierName: string;
  rows: LowStockRow[];
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Up to the maximum, or the reorder quantity (spec 014, FR-1411).
  const orderable = rows.filter((r) => suggestedQty(r) > 0);
  const skipped = rows.length - orderable.length;

  const raise = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/purchase-orders', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          supplier_id: supplierId,
          // No price from the browser. The server reads the last known cost,
          // because this is an inventory screen and cost is not its business.
          lines: orderable.map((r) => ({
            item_id: r.id,
            qty_ordered: suggestedQty(r),
            uom: r.base_uom,
          })),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return res.json() as Promise<{ id: string; ref: string }>;
    },
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['admin', 'purchase-orders'] });
      await qc.invalidateQueries({ queryKey: ['admin', 'low-stock'] });
      // Straight to the order, because a draft nobody looks at is not sent.
      await navigate({ to: '/admin/purchase-orders' });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (orderable.length === 0) {
    return (
      <p className="mt-2 text-[13px] text-muted">
        Nothing here has a reorder quantity set, so there is no quantity to
        order. Set one on the item and it appears here.
      </p>
    );
  }

  return (
    <div className="mt-3">
      {error && <Callout tone="danger" title={error} />}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {/* The supplier's name is in the label, and a name has no length
            limit. A button that cannot wrap made Reorder 9 px wider than a
            phone (pipeline 151, 30 October). */}
        <Button
          onClick={() => raise.mutate()}
          disabled={raise.isPending}
          className="h-auto max-w-full whitespace-normal py-2 text-left [overflow-wrap:anywhere]"
        >
          {raise.isPending
            ? 'Raising...'
            : `Raise a draft order for ${supplierName}`}
        </Button>
        <span className="text-[13px] text-muted">
          {orderable.length} {orderable.length === 1 ? 'line' : 'lines'}, at the
          quantity in the Order column. It is a draft until somebody sends it.
          {skipped > 0 && ` ${skipped} left out for having no maximum and no reorder quantity.`}
        </span>
      </div>
    </div>
  );
}
